import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Loader2, Send, CheckCheck, Eye, MessageSquareReply, TrendingUp, AlertTriangle } from 'lucide-react';
import api from '../lib/api';

function Kpi({ icon: Icon, label, value, sub, color }) {
  return (
    <Card className="border-border/50">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1.5">
          <Icon className={`h-4 w-4 ${color}`} />{label}
        </div>
        <p className="text-2xl font-bold leading-none">{value}</p>
        {sub && <p className="text-[11px] text-muted-foreground mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function FunnelBar({ label, value, pct, color }) {
  return (
    <div data-testid={`wa-funnel-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="font-medium">{label}</span>
        <span className="text-muted-foreground">{value} · {pct}%</span>
      </div>
      <div className="h-2.5 rounded-full bg-muted overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
    </div>
  );
}

export const WhatsAppAnalyticsPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/whatsapp/analytics')
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (!data) return <p className="text-sm text-muted-foreground text-center py-10">Analytics unavailable.</p>;

  const t = data.totals || {};
  const f = data.funnel || {};

  return (
    <div className="space-y-5" data-testid="whatsapp-analytics-panel">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={TrendingUp} label="Reply rate" value={`${t.reply_rate ?? 0}%`} sub={`${t.conversations || 0} conversations`} color="text-violet-500" />
        <Kpi icon={Send} label="Sent" value={t.sent || 0} sub={`last ${data.window_days}d`} color="text-emerald-500" />
        <Kpi icon={CheckCheck} label="Delivered" value={t.delivered || 0} sub={`${f.delivered_pct || 0}% of sent`} color="text-sky-500" />
        <Kpi icon={MessageSquareReply} label="Inbound replies" value={t.inbound || 0} color="text-amber-500" />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Delivery & read funnel</CardTitle>
          <CardDescription>How outbound messages progress from sent → delivered → read</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <FunnelBar label="Sent" value={f.sent || 0} pct={f.sent ? 100 : 0} color="bg-emerald-500" />
          <FunnelBar label="Delivered" value={f.delivered || 0} pct={f.delivered_pct || 0} color="bg-sky-500" />
          <FunnelBar label="Read" value={f.read || 0} pct={f.read_pct || 0} color="bg-violet-500" />
          {(t.failed > 0) && (
            <div className="flex items-center gap-1.5 text-xs text-red-400" data-testid="wa-funnel-failed">
              <AlertTriangle className="h-3.5 w-3.5" />{t.failed} failed ({f.failed_pct || 0}%)
            </div>
          )}
          {(!f.delivered && !f.read) && (
            <p className="text-[11px] text-muted-foreground">Delivery & read receipts populate automatically once a WhatsApp Business number is connected and Meta sends status webhooks.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Per-template performance</CardTitle>
          <CardDescription>Which approved templates earn the most replies</CardDescription>
        </CardHeader>
        <CardContent>
          {(data.per_template || []).length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6" data-testid="wa-templates-perf-empty">No template sends in this window yet.</p>
          ) : (
            <div className="space-y-2" data-testid="wa-templates-perf-list">
              <div className="grid grid-cols-12 gap-2 text-[11px] text-muted-foreground px-2 pb-1 border-b border-border">
                <span className="col-span-5">Template</span>
                <span className="col-span-2 text-right">Sent</span>
                <span className="col-span-2 text-right">Read</span>
                <span className="col-span-3 text-right">Reply rate</span>
              </div>
              {data.per_template.map((p) => (
                <div key={p.template} className="grid grid-cols-12 gap-2 items-center px-2 py-1.5 text-sm" data-testid={`wa-tperf-${p.template}`}>
                  <span className="col-span-5 truncate font-medium">{p.template}</span>
                  <span className="col-span-2 text-right">{p.sent}</span>
                  <span className="col-span-2 text-right text-muted-foreground"><Eye className="h-3 w-3 inline mr-0.5" />{p.read}</span>
                  <span className="col-span-3 text-right">
                    <Badge variant="outline" className={p.reply_rate >= 20 ? 'border-emerald-500/40 text-emerald-500' : 'text-muted-foreground'}>{p.reply_rate}%</Badge>
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default WhatsAppAnalyticsPanel;
